import {
  LayoutDashboard,
  Workflow,
  FlaskConical,
  BarChart3,
  Building2,
  Settings,
  Server,
  Shield,
  FileText,
  Zap,
  MapPin,
  Database,
  CreditCard,
  Globe,
  LineChart,
  BookOpen,
  Bot,
  Rocket,
  Users,
  Activity,
  Library,
  TestTube2,
  Bell,
  ShieldCheck,
  HelpCircle,
  LayoutGrid,
  PackagePlus,
  Layers,
} from "lucide-react";

import type { UserRole } from "@/types/auth";
import type { NavItem } from "@/components/layout/NavMain";

/** Org-level feature flags that gate nav items. Must match a key in
 *  `OrganizationSettings.features` (apps/frontend-new/src/types/auth.ts). */
export type OrgFeatureGate = 'qaAutomation';

export type MainNavItem = NavItem & { minRole?: UserRole };
export type EdgesNavItem = NavItem & { adminAction?: boolean };
export type AdminNavItem = NavItem & {
  superAdminOnly?: boolean;
  /** Show only when the active org has settings.features.<flag> === true.
   *  Super-admins bypass this (they need access for debugging across tenants). */
  requireOrgFeature?: OrgFeatureGate;
};
export type HelpNavItem = NavItem & { external?: boolean };

export const allMainNavItems: MainNavItem[] = [
  { title: "Dashboard",      url: "/",               icon: LayoutDashboard },
  { title: "Certifications", url: "/certifications", icon: ShieldCheck },
  { title: "Quick Tests",    url: "/quick-tests",    icon: Zap,        minRole: "member" },
  { title: "Tests",          url: "/tests",          icon: FlaskConical },
  { title: "Flows",          url: "/flows",          icon: Workflow,   minRole: "member" },
  { title: "eSIM Inventory", url: "/esim-inventory", icon: CreditCard, minRole: "member" },
  { title: "Reports",        url: "/reports",        icon: BarChart3 },
  { title: "Notifications",  url: "/notifications",  icon: Bell },
  { title: "Settings",       url: "/settings",       icon: Settings },
];

// "Edges" is the tenant infrastructure page (#270): owned devices AND the
// SimCheck network's location capacity, stacked on one page. Locations moved
// OUT of the main nav — the hierarchy manager is an admin tool, and tenants
// reach their own sites from the Edges page.
export const allEdgesNavItems: EdgesNavItem[] = [
  { title: "Edges",           url: "/edges",              icon: Server },
  { title: "Connectivity",    url: "/edges/connectivity", icon: Activity },
  { title: "Provisioning",    url: "/edges/provisioning", icon: Shield, adminAction: true },
  { title: "Modem Catalogue", url: "/edges/modems",       icon: Database },
];

export const allAdminItems: AdminNavItem[] = [
  { title: "Organizations",       url: "/admin/organizations",      icon: Building2,  superAdminOnly: true },
  { title: "Plan Catalog",        url: "/admin/subscription-plans", icon: LayoutGrid, superAdminOnly: true },
  { title: "Add-ons",             url: "/admin/billing-addons",     icon: PackagePlus, superAdminOnly: true },
  { title: "Users",               url: "/admin/users",              icon: Users,      superAdminOnly: true },
  // The cross-org global device view. /edges is deliberately scoped to the
  // active org (#270), so without this row a super-admin has no nav path to
  // every tenant's fleet. Gate matches the route (super_admin + support).
  { title: "Edge Devices",        url: "/admin/edge-devices",       icon: Server },
  // The full location estate. Tenants reach their OWN sites from the Edges
  // page's "Manage my sites" instead — the page is role-scoped either way.
  { title: "Locations",           url: "/locations",                icon: MapPin },
  { title: "Deployments",         url: "/admin/deployments",        icon: Rocket,     superAdminOnly: true },
  { title: "Execution Monitor",   url: "/admin/execution-monitor",  icon: Activity,   superAdminOnly: true },
  { title: "Location Analytics",  url: "/admin/location-analytics", icon: LineChart },
  { title: "Networks",            url: "/admin/networks",           icon: Globe,      superAdminOnly: true },
  { title: "Primitives",          url: "/admin/primitives",         icon: Zap,        superAdminOnly: true },
  { title: "MCP Keys",            url: "/admin/mcp-keys",           icon: Bot,        superAdminOnly: true },
  { title: "SGP.32 Provisioning", url: "/admin/provisioning",       icon: Globe,      superAdminOnly: true },
  { title: "Audit Logs",          url: "/admin/audit-logs",         icon: FileText },
  { title: "System",              url: "/admin/system",             icon: Shield,     superAdminOnly: true },
  // Tenant-gated: only visible in orgs whose settings.features.qaAutomation === true.
  // Super-admins see it everywhere (they can debug any tenant's QA setup).
  { title: "QA Automation",       url: "/admin/qa-automation",      icon: TestTube2, requireOrgFeature: 'qaAutomation' },
];

export const helpNavItems: HelpNavItem[] = [
  { title: "Primitive Library", url: "/primitives",       icon: Library },
  { title: "Developer Portal",  url: "/developer-portal", icon: BookOpen },
  { title: "Manual",            url: "/user-manual/",           icon: HelpCircle, external: true },
];

// Routes outside the sidebar that still deserve a meaningful tab title.
// Path → label, prefix-matched in useDocumentTitle.
export const extraTitleRoutes: { url: string; title: string }[] = [
  { url: "/login",           title: "Sign in" },
  { url: "/signup",          title: "Sign up" },
  { url: "/forgot-password", title: "Reset password" },
  { url: "/manual",          title: "User manual" },
];
